"use client"
import React, { useContext, useEffect, useState } from "react"
import { AuthContext } from "../context/AuthContext"
import {
  TrendingUp,
  ShoppingBag,
  Package,
  Clock,
  ArrowRight,
  TrendingDown,
  DollarSign,
  AlertCircle,
} from "lucide-react"
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts"
import { useNavigate } from "react-router-dom"

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000"
const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"]

const Summary = () => {
  const { token } = useContext(AuthContext)
  const navigate = useNavigate()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const res = await fetch(`${BASE_URL}/api/dashboard/summary`, {
          headers: { "x-auth-token": token },
        })
        if (!res.ok) throw new Error("Failed to fetch summary data")
        const jsonData = await res.json()
        setData(jsonData)
      } catch (err) {
        console.error("Summary error:", err)
        setError("Failed to load dashboard data.")
      } finally {
        setLoading(false)
      }
    }
    if (token) fetchData()
  }, [token])

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen text-gray-500">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-2"></div>
      Loading Dashboard...
    </div>
  )

  if (error) return (
    <div className="flex flex-col items-center justify-center min-h-screen text-red-500">
      <AlertCircle className="w-12 h-12 mb-4" />
      <p>{error}</p>
      <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-red-100 rounded-md hover:bg-red-200 text-red-700">Retry</button>
    </div>
  )

  if (!data) return null

  const { stats, salesTrend, statusDist, topProducts, recentOrders } = data

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen max-w-7xl mx-auto space-y-6 md:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
          <p className="text-sm text-gray-500 mt-1">Welcome back! Here's what's happening today.</p>
        </div>
        <button
          onClick={() => navigate("/create-order")}
          className="w-full sm:w-auto inline-flex justify-center items-center px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium shadow-sm transition-all hover:shadow-md"
        >
          Create Order
          <ArrowRight className="ml-2 h-4 w-4" />
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <KPICard
          title="Total Sales"
          value={`Rs. ${stats.totalSales?.toLocaleString()}`}
          icon={<DollarSign className="w-5 h-5 text-indigo-600" />}
          bg="bg-indigo-50"
          trend="+12.5% from last month" // Placeholder trend
          trendUp={true}
        />
        <KPICard
          title="Total Orders"
          value={stats.totalOrders?.toLocaleString()}
          icon={<ShoppingBag className="w-5 h-5 text-blue-600" />}
          bg="bg-blue-50"
        />
        <KPICard
          title="Avg. Order Value"
          value={`Rs. ${stats.avgOrderValue?.toLocaleString()}`}
          icon={<TrendingUp className="w-5 h-5 text-emerald-600" />}
          bg="bg-emerald-50"
        />
        <KPICard
          title="Pending Actions"
          value={stats.pendingOrders?.toLocaleString()}
          icon={<Clock className="w-5 h-5 text-amber-600" />}
          bg="bg-amber-50"
          subtext="Orders needing attention"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales Trend Chart (Width 2/3) */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800">Sales Performance (30 Days)</h3>
            <p className="text-sm text-gray-500">Daily revenue trends</p>
          </div>
          <div className="h-[250px] md:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={salesTrend}>
                <defs>
                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis
                  dataKey="_id"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  tickFormatter={(str) => {
                    const d = new Date(str);
                    return `${d.getDate()}/${d.getMonth() + 1}`;
                  }}
                  minTickGap={30}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  tickFormatter={(val) => `Rs.${val / 1000}k`}
                  width={60}
                />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                  formatter={(val) => [`Rs. ${val.toLocaleString()}`, 'Sales']}
                />
                <Area
                  type="monotone"
                  dataKey="sales"
                  stroke="#4F46E5"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorSales)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Order Status Donut (Width 1/3) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-6">Order Status</h3>
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusDist}
                  dataKey="count"
                  nameKey="_id"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                >
                  {statusDist.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-6">
        {/* Top Products Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 md:p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Top Selling Products</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-600 whitespace-nowrap">
              <thead className="bg-gray-50 text-xs uppercase font-semibold text-gray-500">
                <tr>
                  <th className="px-4 md:px-6 py-3">Product Name</th>
                  <th className="px-4 md:px-6 py-3 text-right">Qty Sold</th>
                  <th className="px-4 md:px-6 py-3 text-right">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {topProducts.map((p, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 md:px-6 py-3 font-medium text-gray-900 max-w-[150px] truncate" title={p._id}>{p._id}</td>
                    <td className="px-4 md:px-6 py-3 text-right">{p.totalQty}</td>
                    <td className="px-4 md:px-6 py-3 text-right">Rs. {p.totalRevenue?.toLocaleString()}</td>
                  </tr>
                ))}
                {topProducts.length === 0 && (
                  <tr><td colSpan="3" className="px-6 py-4 text-center text-gray-400">No product data available</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Orders List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {recentOrders && recentOrders.length > 0 ? (
            <>
              <div className="p-4 md:p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">Recent Orders</h3>
                <button onClick={() => navigate("/all-orders")} className="text-indigo-600 text-sm hover:underline">View All</button>
              </div>
              <div className="divide-y divide-gray-100">
                {recentOrders.map((order) => (
                  <div key={order._id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3 md:gap-4 overflow-hidden">
                      <div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                        <Package className="w-4 h-4 md:w-5 md:h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate max-w-[120px] md:max-w-none">{order.customerName}</p>
                        <p className="text-xs text-gray-500 truncate">#{order.orderId} • {new Date(order.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <p className="text-sm font-bold text-gray-900">Rs. {order.total?.toLocaleString()}</p>
                      <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="p-6 text-center text-gray-400">No recent orders found</div>
          )}
        </div>
      </div>

    </div>
  )
}

const KPICard = ({ title, value, icon, bg, trend, trendUp, subtext }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex items-start space-x-4 transition-all hover:shadow-md">
    <div className={`p-3 rounded-lg ${bg}`}>
      {icon}
    </div>
    <div>
      <p className="text-sm font-medium text-gray-500">{title}</p>
      <h3 className="text-2xl font-bold text-gray-900 mt-1">{value}</h3>
      {trend && (
        <div className={`flex items-center mt-1 text-xs font-medium ${trendUp ? "text-green-600" : "text-gray-400"}`}>
          {trendUp ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
          {trend}
        </div>
      )}
      {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
    </div>
  </div>
)

const getStatusColor = (status) => {
  switch (status) {
    case "New": return "bg-blue-100 text-blue-700"
    case "Packed": return "bg-green-100 text-green-700"
    case "Pending Packed": return "bg-amber-100 text-amber-700"
    case "Cancelled": return "bg-red-100 text-red-700"
    default: return "bg-gray-100 text-gray-600"
  }
}

export default Summary
